
<template lang="pug">
  transition(appear, name="fade")
    Container.all-results(
      :embed="embed",
      :titleProps="titleProps",
      :year="currentYear",
      :error="error",
      :debug="debug",
      :loading="loading",
      :disclaimerConfig="disclaimerConfig",
      :class="{ 'all-results_expanded' : isExpanded, 'all-results_contracted' : !isExpanded }"
      v-if="districts",
    )

      template(#nav, v-if="embed && !hideYearNav")
        YearNav(@update:changeYear="changeYear", :year="currentYear", :embed="embed")

      template(#title)
        .all-results_titlerow(v-if="(showControls || titleProps)")
          SearchInput(v-if="showControls", @inputChange="updateSearchValue")
          GroupTitle(v-if="titleProps?.title", v-bind="titleProps")
          Button(v-if="showControls", :text="isExpanded ? 'See less seats' : 'See all seats'", @handleClick="isExpanded = !isExpanded", :iconFlipped="!isExpanded")
            IconArrowDown

      .tooltip(v-if="!isMobile && !isThisApp && enableToolTip", :style="tooltipStyles", :touch-device="TOUCH_DEVICE", :class="{ isTouchDevice: TOUCH_DEVICE, notTouchDevice: !TOUCH_DEVICE }")
        template(v-if="tooltipData")
          .tooltip_close(@click="hideTooltip")
            CloseIcon
          DistrictDataTable(:district="tooltipData", :isLive="isLive", :year="currentYear")

      .all-results_content

        table.all-results_table(cellspacing="0")
          thead(@click="tooltipData && hideTooltip($event, 'foo')")
            th.all-results_table_th.district_name(@click="onSort('')")
              .all-results_table_th_inner
                span.all-results_table_th_name|Name
                transition(appear, name="fade")
                  template(v-if="sortValue")
                    Button(@handleClick='sortValue = null', :searchValue="searchValue", :text="`Reset sort`", dense)
                      CloseIcon

            th.all-results_table_th(v-for="party in partyColumns", :key="party['code']", :party="party['code']", :title="party['name']", @click="onSort(party['code'])", :class="{ sorted: sortValue === party['code'], ascending: sortAscending }" )
              .all-results_table_th_inner
                PartyLabel(:name="(party['displayCode'] || party['shortName'] || party['code'])", :code="party['code']", :color="party['color']", layout="vertical")
                span.all-results_table_th_icon
                  transition(appear, name="fade")
                    template(v-if="sortValue === party['code']")
                      IconArrowDown

          transition-group(name="flip-list", tag="tbody")
            template(v-for="district in sortedDistricts", :key="district['id']")
              tr(
                :district-id="district['id']",
                :style="{ '--leading-color': district.displayColor }",
                :class="{ 'all-results_table_tr_selected': district.isSelected }"
                @click="tableRowClick(district, $event, TOUCH_DEVICE)",
                @mouseenter="showTooltip(district, $event)",
                @mousemove="moveTooltip(district, $event)",
                @mouseleave="hideTooltip($event, 'foo')"
              )
                td.district_name() {{ district['name'] }}
                td.district_party(v-for="party in partyColumns", :key="party['code']", :class="getCellClassnames(district, party)")
                  span.district_party_value(v-if="district.candidateMap && district.candidateMap[party['code']]")
                    span {{ district.candidateMap[party['code']].Votes.Percentage.toFixed(2) }}

              tr.all-results_table_tr_selected_data(v-if="district.isSelected && tooltipData" )
                td(:colspan="(partyColumns.length + 1)")
                  .tooltip(:style="tooltipStyles", :touch-device="TOUCH_DEVICE", :class="{ isTouchDevice: TOUCH_DEVICE, notTouchDevice: !TOUCH_DEVICE }")
                    template(v-if="tooltipData")
                      .tooltip_close(@click="hideTooltip")
                        CloseIcon
                      DistrictDataTable(:district="tooltipData", :isLive="isLive", :year="currentYear")

</template>
<script>
import { rgb } from 'd3-color';

// import components
import Container from '../../components/Container/Container.vue';
import DistrictDataTable from '../../components/DistrictDataTable/DistrictDataTable.vue';
import GroupTitle from '../../components/GroupTitle/GroupTitle.vue';
import Button from '../../components/Button/Button.vue';
import IconArrowDown from '../../assets/icon-chevron-down.svg';
import CloseIcon from '../../assets/icon-cross.svg';
import PartyLabel from '../../components/PartyLabel/PartyLabel.vue';
import SearchInput from '../../components/SearchInput/SearchInput.vue';

// import mixins
import tooltipMixin from '../../mixins/tooltip.js';
import coreEmbedMixin from '../../mixins/coreEmbed';
import yearNavMixin from '../../mixins/yearNav';

// import utils
import eventTracking from '../../utils/eventTracking';

const body = document.querySelector('body');

export default {
  name: 'AllResults',
  mixins: [ tooltipMixin, coreEmbedMixin, yearNavMixin ],
  props: {
    state: {},
    expanded: {
      type: Boolean,
      default: true,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const isThisApp = Boolean(window.DNA_APP_ENV) ||
                      (body && body.attributes && body.attributes.length === 0) ||
                        document.querySelector('.screenkit--embed-container');
    return {
      searchValue: null,
      sortValue: null,
      sortAscending: false,
      selection: null,
      isExpanded: this.expanded,
      isThisApp
    };
  },
  computed: {
    colors() {
      return this.parties.reduce( (colors, party) => ({
        ...colors,
        [party.code]: party.color
      }), {});
    },
    currentElection() { return this.$store.getters.currentElection(this.currentYear); },
    enableToolTip() { return !this.currentElection.isReferendum },
    districts() {

      const districts = this.$store.getters.districts(this.currentYear).map( ( district ) => {
        const {
          'eml:ContestIdentifier': {
            Id: id,
            'eml:ContestName': name
          }
        } = district;

        // Map array of candidates to an object with the party code as key
        const dataNode = (this.currentElection.isReferendum) ? district?.ProposalResults?.Option : district?.FirstPreferences?.Candidate;
        const candidateMap = dataNode.reduce( (acc, candidate) => {
          const {
            // affiliation,
            affiliationGroup,
            Votes: {
              Count = 0,
              Percentage = 0.00,
              // Swing = 0.00
            }
          } = candidate;

          const displayedAffiliationGroup = this.partyColumns.find( p => {
            if (p.code === affiliationGroup) return true;
            if (p.groupCodes && p.groupCodes.includes(affiliationGroup)) return true;
            if (p.aliasCodes && p.aliasCodes.includes(affiliationGroup)) return true;
            return p.code === 'OTH';
          })?.code || 'OTH';

          const currentDataForParty = acc[displayedAffiliationGroup] || {
            Votes: {
              Count: 0,
              Percentage: 0.00
            }
          };

          acc[displayedAffiliationGroup] = {
            affiliationGroup: displayedAffiliationGroup,
            Votes: {
              Count: (currentDataForParty.Votes.Count + Count),
              Percentage: (currentDataForParty.Votes.Percentage + Percentage)
            }
          };

          return acc;
        }, {});

        const
          sortedData = dataNode.slice().sort( (a, b) => (b.Votes.Percentage - a.Votes.Percentage) ), // b.Votes.count - a.Votes.count;
          leadingParty = sortedData[0].Votes.Percentage > 0 ? sortedData[0].affiliation.code : null,
          leadingColor = (this.colors[leadingParty]),

          { party: calledStatusParty, isCalled } = this.getCalledSeatValue(district),
          calledStatusPartyData = calledStatusParty ? this.$store.getters.getPartyByCodeOrAlias(calledStatusParty, this.currentYear) : null,
          calledPartyColor = isCalled && calledStatusPartyData ? calledStatusPartyData.color : null,
          likelyColor = !isCalled && calledStatusPartyData ? calledStatusPartyData.color : null;

        const leader = calledStatusParty || leadingParty;
        const leadingCol = this.partyColumns.find( (p) => {
          if (p.code === leader) return true;
          if (p.groupCodes && p.groupCodes.includes(leader)) return true;
          if (p.aliasCodes && p.aliasCodes.includes(leader)) return true;
          return p.code === 'OTH';
        } )?.code;

        // return merged object.
        return {
          id,
          name,
          ...district,
          candidateMap,
          displayColor: this.getDisplayColorValue(calledPartyColor, likelyColor, leadingColor),
          leader,
          isCalled,
          leadingCol,
          isSelected: (id === this.selection || name === this.selection)
        };
      }).filter((district) => {
        if (this.state) {
          return district.State === this.state;
        }

        return true;
      });

      districts.sort( (a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      });

      return districts;
    },
    visibleDistricts() {
      return this.searchValue ? this.districts.filter( (district) => {
        const regex = new RegExp(this.searchValue, 'gi');
        return district.name.match(regex);
      } ) : this.districts;
    },
    sortedDistricts() {
      return this.sortValue ? this.visibleDistricts.concat().sort((a, b) => {
        const pc1 = a.candidateMap[this.sortValue] && parseFloat(a.candidateMap[this.sortValue].Votes.Percentage, 10) || 0;
        const pc2 = b.candidateMap[this.sortValue] && parseFloat(b.candidateMap[this.sortValue].Votes.Percentage, 10) || 0;

        return this.sortAscending ? pc1 - pc2 : pc2 - pc1;
      }) : this.visibleDistricts;
    },

    parties() {
      return this.$store.getters.parties(this.currentYear);
    },
    partyColumns() {
      return this.parties.filter(party => {
        return this.isMobile ? party?.options?.showAsTableColumnMobile : party?.options?.showAsTableColumn;
      });
    },
    percentCounted() {
      const percentRollCounted = this.$store.getters.percentCounted(this.currentYear);
      return percentRollCounted ? `${ percentRollCounted }% counted` : null;
    },
    searchInputTrackingData() {
      const analyticsConfig = this.$store.getters.getAnalyticsConfig(this.currentYear);

      return {
        ...analyticsConfig,
        label: 'all-seats-search',
        order: '1',
      };
    },
  },
  mounted() {
    this.tooltip = this.$el?.querySelector('.tooltip');

    console.log('mounted', this.$el, this.$el?.querySelector('.tooltip'));

  },
  methods: {
    getCellClassnames(district, party) {
      const isLeader = district.leadingCol === party.code;

      return {
        isCalled: district.isCalled,
        isLeader,
      };
    },

    onSort(partyCode) {
      // If already sorted by this partCode, invert the sorting or set to default sort order
      this.sortAscending = this.sortValue === partyCode ? !this.sortAscending : false;
      this.sortValue = partyCode;
    },
    inputClick() {
      eventTracking(this.searchInputTrackingData);
    },
    getCalledSeatValue(districtData) {

      const useSplitData = this.$store.getters.currentElection(this.currentYear).useSplitData;
      let status = '';

      if (useSplitData) {
        const statusKey = this.$store.getters.dataMasthead(this.currentYear);

        status = (districtData.ncaCalledStatus && districtData.ncaCalledStatus[statusKey]) || '';
      } else {
        status = (districtData.ncaCalledStatus || '');
      }

      const [ , party ] = (typeof status === 'string') ? (status.match(/(.*)\s(?:gain|retain|win|likely)/i) || [] ) : [];
      const [ , win ] = (typeof status === 'string') ? (status.match(/(.*)\s(?:gain|retain|win)/i) || [] ) : [];
      return { party, isCalled: Boolean(win) };
    },
    getDisplayColorValue(calledPartyColor, likelyColor, leadingColor) {

      if (calledPartyColor) {
        return calledPartyColor;
      } else if (likelyColor) {
        return rgb(likelyColor).copy({ opacity: 0.2 });
      } else if (leadingColor) {
        return rgb(leadingColor).copy({ opacity: 0.2 });
      }
    },
    tableRowClick(district, e, isTouchDevice = false) {
      if (this.isMobile) {
        console.log(district);
        this.selection = district.id;
        this.showTooltip(district, e);
      } else {
        /* EDINT-339 : Added a check for '&& !isTouchDevice' on the 'if' condition to ensure the tooltip only hides on desktop when a row is clicked (so that existing feature is not changed).
        On tablets, the tooltip should remain visible when a row is clicked. Previously, this.tooltipData being true was causing the tooltip to hide on tablets when clicking another row. */
        if (this.tooltipData && !isTouchDevice) {
          // This is here, for the tooltip to automatically close on desktop browser when a row is clicked
          this.hideTooltip(e, 'foo');
        } else {
          this.showTooltip(district, e);
        }
      }
    },
    updateSearchValue(val) {
      console.log('updateSearchValue', val);
      this.searchValue = val;
    }
  },
  components: { DistrictDataTable, GroupTitle, Container, Button, IconArrowDown, CloseIcon, PartyLabel, SearchInput },
};
</script>
<style lang="scss">
  @import "src/core/scss/global-sass";
  .all-results {
    text-align: left;
    width: 100%;
    box-sizing: border-box;
    transition: opacity 0.3 0.3s ease;
    @include font-base-m;
    @include thaustalianFonts();

    @include MQ_SmallOnly() {
      @include font-base-s;
      @include thaustalianFonts();

      .all-results_table_tr_selected_data {
        td {
          padding: 0;
        }
      }

      .all-results_table .tooltip {
        position: relative;
        transform: initial !important;
        left: 0 !important;
        top: 0 !important;
        opacity: 1;
        margin: 0 2px var(--spacing-unit);
        width: 99%;
        width: calc(100% - 4px);
      }

    }

    .year-nav {
      margin: var(--spacing-unit) 0 var(--spacing-unit-x2);
    }


    &_expanded {

    }

    &_contracted &_content {
      max-height: 360px;
      overflow: auto;
    }

    &_titlerow {
      display: flex;
      justify-content: space-between;
      align-items: center;
      > * {
        margin-bottom: var(--spacing-unit-x2);
      }
    }

    &[election-embed] &_titlerow {
      flex-wrap: wrap;
      .election-component-title {
        order: 0;
        flex: 0 0 100%;
      }

      .eui-searchInput {
        order: 1;
      }

      .eui-button {
        order: 2;
      }
    }

    &.loading {
      transition-delay: 0;
      opacity: 0.5;
    }

    &_content {
      overflow: auto;
    }

    &[election-embed] &_content {
      padding: var(--spacing-unit) 0;
    }

    &_filter {
      padding: 0 0 var(--spacing-unit-x2);
      display: flex;
      justify-content: flex-start;

      &_input {
        height: 36px;
        padding: $m-spacing;
        width: 200px;
        border: 1px solid #333;
        box-sizing: border-box;
        font-size: 14px;
      }
      .eui-button { margin-left: var(--spacing-unit); }
    }

    &_table {
      padding:0;
      width: 100%;
      line-height: 1;

      &_th {
        width: 10%;
        cursor: pointer;
        padding: 0 var(--spacing-unit-x0_75);
        white-space: nowrap;
        font-weight: normal;

        &_inner {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-direction: column;

          height: 54px;
        }

        --sort-icon-width: 14px;

        &.district_name &_inner {
          border-color: white;
          flex-direction: row;
          justify-content: flex-start;
          align-items: flex-end;
          padding: 0 0 var(--sort-icon-width);
        }

        &_icon { flex: 0 0 var(--sort-icon-width); width: var(--sort-icon-width); overflow: hidden; }
        &_name {

        }

        .eui-button { margin-left: var(--spacing-unit);  }
        .eui-svg-icon { transition: all 0.3s; width:var(--sort-icon-width);}

        path { fill: currentColor; }

        &.sorted { font-weight:bold; }
        &.ascending .eui-svg-icon { transform: scaleY(-1); }
      }
    }

    tr {
      transition: background-color 0.3s;
      &:nth-child(odd) {
        background-color: #f6f6f6;
      }

      @include MQ_TabletAndUp() {
        &:hover {
            background-color: rgba(0,0,0,0.1);
        }
      }

      margin-top: 1px;
    }

    td {
      color: currentColor; white-space: nowrap;  padding: var(--spacing-unit-x0_75);  width: 10%;
      border-top: 1px solid #fff;

      &.district_name {
        // font-weight: bold;
        color: var(--color-title);
      }
      &.district_party {
        text-align: center;
      }
      &.district_party.isLeader {
        background-color: var(--leading-color);

      }
      &.district_party.isLeader.isCalled {
        color: #fff;
      }
    }
    tr.district_name,
    td.district_name { width: 20%; flex-direction: row; }

    .district_party_value {
      display: block;
    }
  }

  .flip-list-move {
    transition: transform 1s;
    pointer-events: none;
  }


</style>
